import { z } from "zod";

import {
  ORGANIZATION_INVITATION_STATUS,
  ORGANIZATION_TYPE,
  TEAM_MEMBER_ROLE,
} from "~/admins/constants/organizations";
import { TEXT_SHORT_LENGTH } from "~/common/constants";

const organizationBaseResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(ORGANIZATION_TYPE),
  created_at: z.string(),
});

export const organizationAddressSchema = z.object({
  id: z.string().optional(),
  address: z.object({
    street: z.string(),
    apartment: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zipcode: z.string(),
  }),
});

export const organizationContactSchema = z.object({
  id: z.string().optional(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  phone_number: z.string(),
});

export const organizationDetailResponseSchema = organizationBaseResponseSchema
  .omit({ created_at: true })
  .extend({
    internal_note: z.string().nullable(),
    addresses: z.array(organizationAddressSchema),
    contacts: z.array(organizationContactSchema),
  });

export const organizationInvitationStatusSchema = z.nativeEnum(
  ORGANIZATION_INVITATION_STATUS,
);

export const organizationSeatDetailResponseSchema = z.object({
  expires_at: z.string(),
  created_at: z.string(),
  last_status_change_at: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  id: z.string().uuid(),
  invited_by: z.string().uuid(),
  organization_id: z.string().uuid(),
  role: z.nativeEnum(TEAM_MEMBER_ROLE),
  status: organizationInvitationStatusSchema,
  team_member_email: z.string().email(),
});

export const organizationSeatListResponseSchema = z.object({
  created_at: z.string(),
  deactivated_at: z.string().nullable(),
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  team_member_id: z.string().uuid(),
  team_member: z.object({
    email: z.string().email(),
    id: z.string().uuid(),
    last_name: z.string(),
    name: z.string(),
    patients_count: z.number(),
    phone_number: z.string(),
    preferred_reference: z.string(),
    profile_picture_path: z.string().nullable(),
    signature_name: z.string(),
    type: z.nativeEnum(TEAM_MEMBER_ROLE),
  }),
});

export const organizationListResponseSchema =
  organizationBaseResponseSchema.extend({
    seats_count: z.number(),
  });

export const updateOrganizationRequestSchema = organizationBaseResponseSchema
  .pick({ name: true, id: true })
  .extend({
    name: z.string(),
    addresses: z.array(
      organizationAddressSchema.extend({
        id: z.string().optional(),
      }),
    ),
    contacts: z.array(
      organizationContactSchema.extend({
        id: z.string().optional(),
        email: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        phone_number: z.string(),
      }),
    ),
  });

export const updateOrganizationFormSchema = z.object({
  name: z
    .string({
      message: "Name is required",
    })
    .min(1, { message: "Name is required" })
    .max(TEXT_SHORT_LENGTH, "Name must be less than 200 characters"),
  street: z
    .string({
      message: "Street address is required",
    })
    .min(1, { message: "Street address is required" })
    .max(TEXT_SHORT_LENGTH, "Street must be less than 200 characters"),
  apartment: z
    .string()
    .max(TEXT_SHORT_LENGTH, "Apartment must be less than 200 characters")
    .optional(),
  city: z
    .string({
      message: "City is required",
    })
    .min(1, { message: "City is required" })
    .max(TEXT_SHORT_LENGTH, "City must be less than 200 characters"),
  state: z
    .string({ message: "State is required" })
    .min(1, { message: "State is required" }),
  zipcode: z
    .string()
    .min(5, { message: "Zipcode must contain at least 5 numbers" }),
  primaryContactEmail: z.string().min(1, "Email is required").email(),
  primaryContactFirstName: z
    .string({ message: "Contact name is required" })
    .min(1, { message: "Contact name is required" })
    .max(TEXT_SHORT_LENGTH, "Contact name must be less than 200 characters"),
  primaryContactLastName: z
    .string({ message: "Contact last name is required" })
    .min(1, { message: "Contact last name is required" })
    .max(
      TEXT_SHORT_LENGTH,
      "Contact last name must be less than 200 characters",
    ),
  primaryContactPhoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, {
      message: "Must be a valid phone number",
    }),
});

export const addOrganizationRequestSchema = organizationBaseResponseSchema.pick(
  {
    name: true,
    type: true,
  },
);

export const addOrganizationFormSchema = addOrganizationRequestSchema.extend({
  name: z
    .string()
    .min(1, "Name is required")
    .max(TEXT_SHORT_LENGTH, "Name must be less than 200 characters"),
  type: z.nativeEnum(ORGANIZATION_TYPE, { message: "Type is required" }),
});

export const addOrganizationSeatRequestSchema = z.object({
  email: z.string().email(),
  role: z.nativeEnum(TEAM_MEMBER_ROLE),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  organization_id: z.string().uuid(),
});

export const addOrganizationSeatFormSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(TEXT_SHORT_LENGTH, "First name must be less than 200 characters"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(TEXT_SHORT_LENGTH, "Last name must be less than 200 characters"),
  email: z.string().email("Must be a valid email"),
  role: z.nativeEnum(TEAM_MEMBER_ROLE, { message: "Role is required" }),
});

export const updateOrganizationPrimaryProviderFormSchema = z.object({
  primaryProvider: z.string({ required_error: "Provider is required" }).min(1),
});
